import { defineStore } from 'pinia';
import axios from 'axios';
import { supabase } from '@/services/supabase';
import { useFormStore } from '@/store/formStore';
import { useIntegrationStore } from '@/store/integrationStore';
import OrganizationService from '@/services/organizationService';
import { useUIStore } from '@/store/uiStore';
import { useRouter } from 'vue-router';
import { anonymizeData, isAnonymousMode, maskAuthUser } from '@/utils/anonymousMode';

function applyAnonymousToUser(user) {
  return user && isAnonymousMode() ? maskAuthUser(user) : user;
}

function applyAnonymousToOrgs(organizations) {
  if (!organizations || !isAnonymousMode()) return organizations;
  return anonymizeData(organizations);
}

// Constants
const SESSION_TIMEOUT = 10000; // 10 seconds
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    userData: null,
    organizations: null,
    currentOrganization: null,
    emailVerificationPending: false,
    sessionStatus: 'idle', // 'idle' | 'loading' | 'authenticated' | 'unauthenticated'
    lastFetch: null,
    authStateChangeQueue: [],
    isProcessingAuthChange: false,
    authStateChangeListener: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user && state.sessionStatus === 'authenticated',
    currentOrganizationId: (state) => state.currentOrganization?.id,
    isCurrentOrganizationSuspended: (state) =>
      state.currentOrganization?.status === 'suspended',
    // Per organization, switched on by platform admins (API: salesOrderService.isAuthoritative).
    salesOrdersEnabled: (state) =>
      state.currentOrganization?.settings?.sales_order_authoritative === true,
    userRole: (state) => {
      if (!state.currentOrganization || !state.userData?.organizationMemberships) {
        return null;
      }
      const membership = state.userData.organizationMemberships.find(
        (m) => m.organizationId === state.currentOrganization.id
      );
      return membership?.role;
    },
  },

  actions: {
    async processAuthStateChangeQueue() {
      if (this.isProcessingAuthChange || this.authStateChangeQueue.length === 0) return;

      this.isProcessingAuthChange = true;
      try {
        const event = this.authStateChangeQueue.shift();
        await this.handleAuthStateChange(event.type, event.session);
      } finally {
        this.isProcessingAuthChange = false;
        if (this.authStateChangeQueue.length > 0) {
          await this.processAuthStateChangeQueue();
        }
      }
    },

    async verifySession(session) {
      try {
        const { data, error } = await supabase.auth.getUser(session?.access_token);
        return !error && data?.user;
      } catch {
        return false;
      }
    },

    async refreshSession() {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Session refresh error:', error);
        await this.clearUserState();
        throw error;
      }

      if (!session) {
        // Don't clear state immediately if no session, let the caller decide
        return null;
      }

      // Verify the session is valid
      const isValid = await this.verifySession(session);
      if (!isValid) {
        await this.clearUserState();
        throw new Error('Invalid session');
      }

      return session;
    },

    async retryOperation(operation, maxAttempts = MAX_RETRY_ATTEMPTS) {
      let lastError;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          return await operation();
        } catch (error) {
          lastError = error;
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
          }
        }
      }
      throw lastError;
    },

    async fetchUserData() {
      try {
        const authUser = this.user;
        if (!authUser) throw new Error('No authenticated user found');

        // Combine all data fetching into a single operation with timeout
        const fetchOperation = async () => {
          // Fresh timeout per race — a shared rejected Promise breaks later races.
          const createTimeout = () =>
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Database query timeout')), SESSION_TIMEOUT)
            );

          // Fetch user data from /api/users/me endpoint (includes billing data)
          const meResponsePromise = axios.get('/users/me').catch((error) => {
            // If /api/users/me fails, fall back to Supabase
            console.warn('⚠️ /api/users/me endpoint failed, falling back to Supabase:', error);
            return null;
          });

          // Super admins get every organization via include_all; regular users
          // still only get memberships. Used for the org switcher.
          const orgsResponsePromise = axios
            .get('/users/organizations', { params: { include_all: true } })
            .catch((error) => {
              console.warn('⚠️ /users/organizations failed, falling back to Supabase:', error);
              return null;
            });

          // Fallback: Supabase queries (for backward compatibility)
          const userDataPromise = supabase.from('users').select('*').eq('id', authUser.id).single();
          const orgDataPromise = supabase
            .from('user_organizations')
            .select(`*, organizations!user_organizations_organization_id_fkey(*)`)
            .eq('user_id', authUser.id);

          // Try API first, fallback to Supabase if it fails
          let userDataFromMe = null;
          let organizationsFromApi = null;
          try {
            const [meResponse, orgsResponse] = await Promise.race([
              Promise.all([meResponsePromise, orgsResponsePromise]),
              createTimeout(),
            ]);
            if (meResponse && meResponse.data) {
              userDataFromMe = meResponse.data;
            }
            if (orgsResponse && Array.isArray(orgsResponse.data)) {
              organizationsFromApi = applyAnonymousToOrgs(orgsResponse.data);
            }
          } catch (error) {
            console.warn('⚠️ /users/me or /users/organizations failed or timed out:', error);
          }

          // If /api/users/me endpoint succeeded, use that data
          if (userDataFromMe) {
            let organizations = organizationsFromApi;
            if (!organizations) {
              if (userDataFromMe.organizations) {
                organizations = applyAnonymousToOrgs(userDataFromMe.organizations);
              } else {
                try {
                  const orgData = await Promise.race([orgDataPromise, createTimeout()]);
                  if (!orgData.error && orgData.data) {
                    organizations = applyAnonymousToOrgs(
                      orgData.data.map((uo) => ({
                        ...uo.organizations,
                        role: uo.role,
                      }))
                    );
                  }
                } catch (error) {
                  console.warn('⚠️ Failed to fetch organizations from Supabase:', error);
                }
              }
            }
            if (!organizations) organizations = [];

            // Process and update state atomically
            this.$patch((state) => {
              state.userData = userDataFromMe;
              state.organizations = organizations;
              const matchedOrg =
                organizations.length > 0
                  ? organizations.find(
                      (org) => org.id === userDataFromMe.current_organization_id
                    ) || organizations[0]
                  : null;
              state.currentOrganization = matchedOrg
                ? {
                    ...matchedOrg,
                    status: matchedOrg.status || userDataFromMe.organization_status || null,
                  }
                : null;
              state.lastFetch = Date.now();
            });
          } else {
            // Fallback to Supabase if /api/users/me endpoint failed
            const [userData, orgData] = await Promise.all([
              Promise.race([userDataPromise, createTimeout()]),
              Promise.race([orgDataPromise, createTimeout()]),
            ]);

            if (userData.error) throw userData.error;
            if (orgData.error) throw orgData.error;

            // Process and update state atomically
            this.$patch((state) => {
              state.userData = userData.data;
              state.organizations = applyAnonymousToOrgs(
                organizationsFromApi ||
                  orgData.data?.map((uo) => ({
                    ...uo.organizations,
                    role: uo.role,
                  })) ||
                  []
              );
              state.currentOrganization =
                state.organizations.length > 0
                  ? state.organizations.find(
                      (org) => org.id === state.userData.current_organization_id
                    ) || state.organizations[0]
                  : null;
              state.lastFetch = Date.now();
            });
          }

          // Warm up form/integration caches in the background. These endpoints
          // are slow (multi-second) and not required to render the layout, the
          // sidebar, or the role-based default redirect. Awaiting them here
          // would gate the entire auth/init flow — and therefore the router's
          // first navigation — on the slowest of the two responses, which is
          // exactly the white-screen-on-refresh symptom we want to avoid.
          // Pages that need this data already call fetchForms/fetchIntegrations
          // on mount with built-in caching (see formStore.fetchForms), so a
          // background prefetch is enough to keep them snappy without blocking.
          useFormStore()
            .fetchForms()
            .catch((error) => {
              console.warn('Background fetchForms failed:', error);
            });
          useIntegrationStore()
            .fetchIntegrations()
            .catch((error) => {
              console.warn('Background fetchIntegrations failed:', error);
            });
        };

        await this.retryOperation(fetchOperation);
      } catch (error) {
        console.error('Error in fetchUserData:', error);
        throw error;
      }
    },

    async handleAuthStateChange(event, session) {
      switch (event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
          if (session && (!this.user || this.user.id !== session.user.id)) {
            if (await this.verifySession(session)) {
              this.user = applyAnonymousToUser(session.user);
              await this.fetchUserData();
              this.sessionStatus = 'authenticated';
            }
          } else if (session && this.user) {
            // Ensure session status is set if user exists
            this.sessionStatus = 'authenticated';
          }
          break;

        case 'SIGNED_OUT':
          await this.clearUserState();
          break;

        case 'TOKEN_REFRESHED':
          if (session && (await this.verifySession(session))) {
            this.sessionStatus = 'authenticated';
          }
          break;

        case 'USER_UPDATED':
          if (session && (await this.verifySession(session))) {
            this.user = applyAnonymousToUser(session.user);
            await this.fetchUserData();
            this.sessionStatus = 'authenticated'; // Ensure status is set after user update
          }
          break;
      }
    },

    async init() {
      this.sessionStatus = 'loading';

      try {
        // Set up auth state change listener if not already set
        if (!this.authStateChangeListener) {
          this.authStateChangeListener = supabase.auth.onAuthStateChange((event, session) => {
            this.authStateChangeQueue.push({ type: event, session });
            this.processAuthStateChangeQueue();
          });
        }

        // Initial session check
        const session = await this.refreshSession();
        if (!session) {
          this.sessionStatus = 'unauthenticated';
          return false;
        }

        // Set user and fetch data
        this.user = applyAnonymousToUser(session.user);

        try {
          await this.fetchUserData();
          this.sessionStatus = 'authenticated';
          return true;
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          // Don't clear state here, just return false
          this.sessionStatus = 'unauthenticated';
          return false;
        }
      } catch (error) {
        console.error('Error in init:', error);
        this.sessionStatus = 'unauthenticated';
        await this.clearUserState();
        return false;
      }
    },

    async login(email, password) {
      this.sessionStatus = 'loading';
      try {
        const {
          data: { user, session },
          error,
        } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error?.message === 'Email not confirmed') {
          this.emailVerificationPending = true;
          localStorage.setItem('emailVerificationPending', 'true');
          const uiStore = useUIStore();
          const router = useRouter();
          uiStore.addModal(false, {
            title: 'Welcome to Funnelflow!',
            description: 'Please check your email to verify your account.',
            submitText: 'OK',
            onSubmit: () => {
              router.push('/verify-email');
            },
          });
          return { verified: false, message: 'Please verify your email to continue' };
        }

        if (error) throw error;

        if (session && (await this.verifySession(session))) {
          this.user = applyAnonymousToUser(user);
          await this.fetchUserData();
          this.sessionStatus = 'authenticated';
          return user;
        }

        throw new Error('Session verification failed');
      } catch (error) {
        this.sessionStatus = 'unauthenticated';
        console.error('Error logging in:', error);
        throw error;
      }
    },

    async logout() {
      try {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;

        this.clearUserState();
      } catch (error) {
        console.error('Error logging out:', error);
        throw error;
      }
    },

    async clearUserState() {
      this.$patch({
        user: null,
        userData: null,
        organizations: null,
        currentOrganization: null,
        sessionStatus: 'unauthenticated',
        lastFetch: null,
      });
    },

    async handleEmailVerification() {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');

      if (accessToken && type === 'signup') {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: hashParams.get('refresh_token'),
        });

        if (error) throw error;

        this.user = applyAnonymousToUser(data.user);
        await this.fetchUserData();
        return true;
      }
      return false;
    },

    async resendVerificationEmail() {
      try {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: this.user.email,
        });

        if (error) throw error;
        return { success: true, message: 'Verification email sent' };
      } catch (error) {
        console.error('Error sending verification email:', error);
        throw error;
      }
    },

    async checkEmailVerification() {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();
        if (error) throw error;

        if (user?.email_confirmed_at) {
          this.emailVerificationPending = false;
          localStorage.removeItem('emailVerificationPending');
        }
      } catch (error) {
        console.error('Error checking email verification:', error);
      }
    },

    async handleInviteSignup(signupData) {
      await supabase.auth.setSession(signupData.session);
      await this.fetchUserData();
      this.user = applyAnonymousToUser(signupData.user);
      this.organization = applyAnonymousToOrgs(signupData.organization);
    },

    async switchOrganization(organizationId) {
      try {
        const { data } = await axios.post('/users/switch-organization', {
          organizationId,
        });

        // Update local state
        this.currentOrganization = this.organizations.find((org) => org.id === organizationId);

        await this.fetchUserData();

        await supabase.auth.getSession();

        // Refresh the page to ensure all components are updated
        // window.location.reload();

        return this.currentOrganization;
      } catch (error) {
        console.error('Error switching organization:', error);
        throw error;
      }
    },
    async updateOrganization(organizationId, data) {
      try {
        const organizationService = new OrganizationService();

        const response = await organizationService.update(organizationId, data);
        this.currentOrganization = {
          ...this.currentOrganization,
          name: response.name,
          language: response.language,
          settings: response.settings,
          crm_settings: response.crm_settings,
          brand_settings: response.brand_settings,
          address: response.address,
          // Backend merges proposal_defaults server-side (2026-05-12 release),
          // so the response always contains the fully resolved object. We
          // need to mirror it into currentOrganization or subsequent reads
          // of e.g. prices_include_vat / valid_days will be stale until
          // the session is reinitialised.
          proposal_defaults:
            response.proposal_defaults ?? this.currentOrganization?.proposal_defaults ?? null,
          bic: response.bic ?? this.currentOrganization?.bic ?? null,
          bank_account: response.bank_account ?? this.currentOrganization?.bank_account ?? null,
        };
        return response;
      } catch (error) {
        console.error('Error updating organization:', error);
        throw error;
      }
    },
    async createOrganization(data) {
      const response = await axios.post('/organizations', data);
      window.location.reload();
      return response.data;
    },

    // Weekly digest / notification preferences. These live on
    // `user_organizations.notification_settings` (per user × org), not on the
    // organization itself — see GET /users/organizations and
    // PATCH /users/notification-settings.
    async fetchUserOrganizations() {
      const { data } = await axios.get('/users/organizations');
      return data;
    },

    // Updates the preference for the CURRENT organization only. The backend
    // shallow-merges `notification_settings`, but we always send the complete
    // `weekly_digest` object to avoid clobbering it. Caller must have switched
    // to the target org first.
    async updateNotificationSettings(notificationSettings) {
      const { data } = await axios.patch('/users/notification-settings', {
        notification_settings: notificationSettings,
      });

      // Mirror the resolved value into currentOrganization so subsequent reads
      // (e.g. role checks, badges) stay in sync without a full refetch.
      if (this.currentOrganization && data.organization_id === this.currentOrganization.id) {
        this.currentOrganization = {
          ...this.currentOrganization,
          notification_settings: data.notification_settings,
          weekly_digest_enabled: data.weekly_digest_enabled,
        };
      }

      return data;
    },
  },
});
